import React from 'react';
import {
  Typography,
  Box,
  Container,
  Button,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Paper,
} from '@mui/material';
import { Link } from 'react-router-dom';
import FeatureHighlights from '../components/feature-highlights/FeatureHighlights';

import bgImage from '../assets/images/home-page-bg.jpg';
import { appDetails } from '../utils/appDetails';

const HomePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.7)), url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {/* Hero Content */}
      <Container maxWidth="lg" sx={{ flexGrow: 1, py: 12 }}>
        <Box textAlign="center">
          <Typography
            variant={isMobile ? 'h4' : 'h2'}
            component="h1"
            sx={{
              fontWeight: 700,
              fontFamily: 'Carter One, sans-serif',
              mb: 3,
            }}
          >
            Creating apps with a purpose and vision
          </Typography>

          <Card
            sx={{
              maxWidth: 800,
              margin: '0 auto',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              boxShadow: 3,
              backdropFilter: 'blur(6px)',
              mb: 4,
            }}
          >
            <CardContent>
              <Typography
                variant={isMobile ? 'body1' : 'h6'}
                sx={{ color: '#fff' }}
              >
                Our mission is to craft mobile and web experiences that provide our users with rich, engaging, and intuitive interactions. 
                We focus on delivering high-quality applications that not only meet but exceed user expectations.
              </Typography>
            </CardContent>
          </Card>

          <Button
            component={Link}
            to="/apps"
            size="large"
            variant="contained"
            sx={{
              px: 5,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 700,
              backgroundColor: '#ff5722',
              borderRadius: 3,
              '&:hover': {
                backgroundColor: '#e64a19',
              },
            }}
          >
            Explore Our Work
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            justifyContent: 'center',
            mt: 6,
          }}
        >
          {Object.entries(appDetails).map(([slug, app]) => (
            <Paper
              key={slug}
              elevation={3}
              sx={{
                width: 280,
                p: 3,
                borderRadius: 3,
                textAlign: 'center',
                backgroundColor: '#fff',
              }}
            >
              <img
                src={app.imageUrl}
                alt={app.title}
                style={{
                  width: '100%',
                  height: 220,
                  objectFit: 'cover',
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              />
              <Typography variant="h6" fontWeight={600}
              noWrap
              sx={{
                fontSize: 'clamp(0.8rem, 1.2vw, 2rem)', // min 1.2rem, scales with viewport, max 2rem
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {app.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {app.description}
              </Typography>
              <Button
                component={Link}
                to={`/apps/${slug}`}
                variant="contained"
                fullWidth
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#ff5722',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#e64a19',
                  },
                }}
              >
                View App
              </Button>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* Benefits Strip */}
      <Box
        sx={{
          backgroundColor: '#1e1e1e',
          py: 3,
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <FeatureHighlights />
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
